import type { ProjectBrief, Task } from '@dyspatch/core';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Clock, LayoutGrid, List, ScrollText } from 'lucide-react';
import { Age, LoadError, RepoChip, RowSkeleton, TaskKey } from '@/components/common/bits';
import { ProgressBar } from '@/components/common/ProgressBar';
import { SortMenu } from '@/components/common/SortMenu';
import { BriefIcon } from '@/components/common/StatusIcon';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Badge } from '@/components/ui/badge';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import { PausedChip } from '@/features/actions/PauseActions';
import { NewBriefButton } from '@/features/briefs/BriefComposer';
import { TaskDots } from '@/features/graph/TaskDots';
import { Failures } from '@/features/jobs/Failures';
import { NeedsPlan } from '@/features/jobs/NeedsPlan';
import { TaskBoardSection } from '@/features/tasks/TaskBoardSection';
import { useBriefs, useTasks } from '@/hooks/use-fleet';
import { type RowProps, useRowKeys } from '@/hooks/use-row-keys';
import { useSort } from '@/hooks/use-sort';
import { useViewPreference } from '@/hooks/use-view-preference';
import { TASK_LIST_LIMIT } from '@/lib/api';
import {
  type BriefProgress,
  briefGroups,
  briefProgress,
  briefsAwaitingPlan,
  openBriefs,
  producedNote,
  showsProgress,
  waitingReason,
} from '@/lib/briefs';
import { minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { byNumber, byText, byTime, type SortOption } from '@/lib/sorting';
import { cn } from '@/lib/utils';
import type { JobsSearch } from '@/router';

/**
 * Jobs: everything anybody opened, and everything that became of it.
 *
 * The page absorbed two regions from the old home page. Drafts nobody is planning
 * and failures in the last day were both sitting under the gate list, which read
 * them as gates; neither is one. Nothing is waiting on a decision in either case,
 * and a job that stalled before planning or fell over during it is a job, so it
 * belongs on the page about jobs.
 *
 * Group headings sit on the page ground rather than inside a bar at the top of a
 * bordered card, which is the change every list in the console made: a heading
 * that has to be read is 17px on the floor, not 13px inside a rule.
 *
 * **This is a list, and only a list.** It used to be a list with a 400px rail
 * holding the selected job, and everything about a job had to fit that rail. A
 * job is now its own page (`routes/job.tsx`), so the rail is gone and the rows
 * are links into it: from here, from search, and from a link somebody pasted in
 * Slack you land on the same page.
 */
function JobRow({
  brief,
  progress,
  steps,
  paused,
  rowProps,
  waiting,
}: {
  brief: ProjectBrief;
  /** Only passed once the job is dispatchable; see `BriefsPage`. */
  progress?: BriefProgress;
  /** The same steps the fraction counts, for the dots beside it. */
  steps?: readonly Task[];
  /** Any step of it carries the pause flag (docs/JOB-LIFECYCLE.md 2.5). */
  paused?: boolean;
  /**
   * Why this job is dispatched and not moving, when it is not. Present only for
   * the Waiting group, because the clause is what that group is for: a job in
   * that group with nothing on the row saying why is the bug this replaced
   * (issue #21).
   */
  waiting?: string | null;
  /** The list's roving focus; the row is its own link, so Enter needs no help. */
  rowProps: RowProps;
}) {
  const isGate = brief.status === 'awaiting_approval';
  return (
    <li>
      <Link
        to="/jobs/$briefId"
        params={{ briefId: brief.id }}
        {...rowProps}
        className={cn(
          'relative flex items-baseline gap-2.5 px-gutter py-2.5 transition-colors duration-100',
          'hover:bg-raised/60',
        )}
      >
        {isGate ? <span className="bg-gate absolute inset-y-0 left-0 w-[3px]" aria-hidden /> : null}
        {/* A spinner over a job that is queued behind a full fleet is the same
            false claim the group heading used to make, one row lower down. A
            waiting job wears the clock every held step wears. */}
        {waiting ? (
          <Clock className="text-fg-3 size-4 shrink-0 translate-y-0.5" aria-hidden />
        ) : (
          <BriefIcon status={brief.status} className="translate-y-0.5" />
        )}
        <div className="min-w-0 flex-1">
          {/* Line one is the subject and the demand: what this job is, and
              whether it wants something from the reader. Nothing else. */}
          <div className="flex min-w-0 items-baseline gap-2.5">
            <TaskKey>{shortKey(brief.id)}</TaskKey>
            <span className="min-w-0 flex-1 truncate text-base" title={brief.title}>
              {brief.title}
            </span>
            {isGate ? (
              <Badge variant="gate" className="shrink-0 self-center">
                Needs approval
              </Badge>
            ) : null}
            {paused ? <PausedChip className="shrink-0 self-center" /> : null}
            <span className="shrink-0 self-center">
              <Age minutes={minutesSince(brief.updatedAt)} hot={isGate} />
            </span>
          </div>
          {/* Line two is one line, and it is where the row used to spend two.
              The progress sat on a row of its own and the repo and the author on
              another, so an eight-job page was twenty-four lines of which eight
              were titles. They are the same fact about the same job and they
              read as one clause. */}
          <div className="mt-0.5 flex min-w-0 items-center gap-x-2.5 gap-y-1 overflow-hidden">
            <JobProgress progress={progress} steps={steps} />
            {waiting ? <span className="text-fg-3 shrink-0 text-xs">{waiting}</span> : null}
            <RepoChip repo={brief.repos[0] ?? '-'} extra={brief.repos.length - 1} />
            {/* The plan summary lived on this row and made every job three lines
                tall. It is one click away on the job's page, which is where it
                can be read. The source (`github`, `manual`) went the same way:
                it is provenance nobody acts on from a list. */}
            <span className="text-fg-3 min-w-0 truncate text-xs" title={brief.submittedBy}>
              {shortActor(brief.submittedBy)}
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}

/**
 * `sam.tripathi@nymbl.app` is nineteen characters of which the last eleven are
 * the same on every row in the fleet. The address is kept on the title, for the
 * one time somebody needs it.
 */
function shortActor(actor: string): string {
  const at = actor.indexOf('@');
  return at > 0 ? actor.slice(0, at) : actor;
}

/**
 * How far through a job is, sized to the plan rather than drawn the same way
 * whatever the plan is.
 *
 * A one-step job is every job that came in from a labelled issue, and for those
 * the bar was the worst thing on the row: an empty grey track under the words
 * "0 of 1 done", which reads as a broken widget rather than as a step that has
 * not finished. One dot in the step's own colour says the whole of it.
 *
 * A real plan gets the bar, the fraction and the dots, because those answer
 * three different questions: how far, how big, and what shape (which steps are
 * done, which are running, which are waiting on a person; 2.8). The word "done"
 * comes off the fraction: the bar already says which end is finished.
 */
function JobProgress({ progress, steps }: { progress?: BriefProgress; steps?: readonly Task[] }) {
  if (!progress?.total) return null;
  if (progress.total === 1) {
    return (
      <span className="flex shrink-0 items-center gap-1.5">
        {steps ? <TaskDots tasks={steps} /> : null}
        <span className="text-fg-3 shrink-0 text-xs">1 task</span>
      </span>
    );
  }
  // A held step keeps its branch and PR (14 days, docs/JOB-LIFECYCLE.md
  // decision 2), so a job stuck with three of them open still needs to read as
  // different from a job nothing has touched (issue #29). The one-step case
  // above says the same thing with the colour of its single dot.
  const pushedNote = producedNote(progress);
  return (
    <span className="flex shrink-0 items-center gap-2">
      <ProgressBar fraction={progress.done / progress.total} className="w-14" />
      <span className="text-fg-3 tnum shrink-0 text-xs">
        {progress.done} of {progress.total}
        {pushedNote ? ` · ${pushedNote}` : ''}
      </span>
      {steps ? <TaskDots tasks={steps} /> : null}
    </span>
  );
}

/**
 * What a job list can be ordered by (issue #74).
 *
 * `updated` is first, so it is the default, and it is the order the hub returns
 * anyway: a reader who never touches the control sees exactly what they saw
 * before this existed. The rest are the questions the page could not answer at
 * twenty rows: which of these has been sitting longest, whose are they, and how
 * far through is each one.
 *
 * Progress needs the step rows, which the page only has when the task list is
 * not truncated, so it is passed in rather than read off the brief.
 */
function jobSortOptions(
  progressOf: (brief: ProjectBrief) => BriefProgress | undefined,
): SortOption<ProjectBrief>[] {
  return [
    {
      value: 'updated',
      label: 'Last activity',
      compare: byTime((b) => b.updatedAt),
      natural: 'desc',
    },
    { value: 'opened', label: 'Opened', compare: byTime((b) => b.createdAt), natural: 'desc' },
    { value: 'title', label: 'Title', compare: byText((b) => b.title) },
    {
      value: 'progress',
      label: 'Progress',
      // A job with no countable steps sorts as nothing done, which is what it
      // is: the fraction is unknown, and guessing it in the middle would put it
      // above jobs that have genuinely finished work.
      compare: byNumber((brief) => {
        const progress = progressOf(brief);
        return progress?.total ? progress.done / progress.total : 0;
      }),
      natural: 'desc',
    },
    { value: 'repo', label: 'Repo', compare: byText((b) => b.repos[0] ?? '') },
    { value: 'opener', label: 'Opened by', compare: byText((b) => b.submittedBy) },
  ];
}

/**
 * List or board, for the one nav entry Work now has for a job's tasks
 * (issue #60): "Jobs" carries both, rather than a second entry ("Steps") that
 * existed only to be a different shape of the same fleet.
 *
 * Two buttons rather than a `Menu`, matching `RangeControl` below: a reader
 * flips this far more often than a menu is worth opening for, and the choice
 * is binary.
 */
function ViewToggle({ board, onChange }: { board: boolean; onChange: (board: boolean) => void }) {
  return (
    <fieldset className="border-line-2 flex items-center gap-px rounded-md border p-px">
      <legend className="sr-only">Jobs as a list or as a task board</legend>
      <button
        type="button"
        onClick={() => onChange(false)}
        aria-pressed={!board}
        title="List"
        className={cn(
          'rounded-[4px] p-1.5 transition-colors duration-100',
          !board ? 'bg-raised text-foreground' : 'text-fg-3 hover:text-foreground',
        )}
      >
        <List className="size-4" aria-hidden />
        <span className="sr-only">List</span>
      </button>
      <button
        type="button"
        onClick={() => onChange(true)}
        aria-pressed={board}
        title="Task board"
        className={cn(
          'rounded-[4px] p-1.5 transition-colors duration-100',
          board ? 'bg-raised text-foreground' : 'text-fg-3 hover:text-foreground',
        )}
      >
        <LayoutGrid className="size-4" aria-hidden />
        <span className="sr-only">Task board</span>
      </button>
    </fieldset>
  );
}

export function BriefsPage() {
  const search = useSearch({ strict: false }) as JobsSearch;
  const navigate = useNavigate();
  // The board is what a viewer opens Jobs to answer -- what is moving, what
  // is stuck, what is waiting on them (issue #98) -- so it is the default a
  // first-time viewer gets. `search.board` is the URL's own opinion, for a
  // link that has to keep landing where it says it does (a `/tasks` redirect,
  // one saved with `?board=false`); its absence defers to the standing choice
  // this viewer already made, not to a hardcoded default.
  const viewPreference = useViewPreference('jobs.board', true);
  const board = search.board ?? viewPreference.value;

  /**
   * Switching views replaces the history entry rather than stacking one: it is
   * a display choice, not a place, same as every filter on the old `/tasks`.
   * Leaving the board clears its selection (`task`) too, so coming back to the
   * list and then to the board later opens with nothing pinned open rather
   * than a stale id from a different visit.
   *
   * The toggle is the one place a flip here is a viewer's own choice rather
   * than a visit's: it writes the preference so the next bare `/jobs` opens
   * on whichever view this button just chose.
   */
  const setBoard = (next: boolean) => {
    viewPreference.set(next);
    void navigate({
      to: '.',
      search: (previous: JobsSearch) => ({
        ...previous,
        board: next ? true : undefined,
        task: next ? previous.task : undefined,
      }),
      replace: true,
    });
  };
  const toggle = <ViewToggle board={board} onChange={setBoard} />;

  const briefs = useBriefs();
  const tasks = useTasks();
  // `repo` narrows the list the same way it already narrows the board
  // (`TaskBoardSection`): a project page links here for "every job naming
  // this repo" rather than drawing that list a second time.
  const rows = (briefs.data ?? []).filter(
    (brief) => !search.repo || brief.repos.includes(search.repo),
  );

  // One pass over the task list rather than a query per row: the list is already
  // in the cache for the steps page and the palette, and a job's fraction is just
  // its own steps counted.
  const taskRows = tasks.data ?? [];
  const tasksByBrief = new Map<string, Task[]>();
  for (const task of taskRows) {
    const bucket = tasksByBrief.get(task.briefId);
    if (bucket) bucket.push(task);
    else tasksByBrief.set(task.briefId, [task]);
  }
  // ...but only while that list is the whole list. The hub caps this route and
  // returns the *oldest* rows, so a full page means some job's steps are missing
  // and there is no telling whose. "3 of 4 done" on a job that has seven steps is
  // worse than no bar at all, so the bar goes away instead.
  const countable = taskRows.length < TASK_LIST_LIMIT;

  // "Stalled before planning" draws these already, with the verb on each row.
  // `briefGroups` takes the ids so "In progress" does not print the same job a
  // second time forty pixels lower.
  const stalled = briefsAwaitingPlan(rows, taskRows);
  const groups = briefGroups(rows, taskRows, new Set(stalled.map((brief) => brief.id)));

  /**
   * The order is chosen by the reader and applied inside each group, never
   * across them. The grouping is the page's answer to "what needs me", from #21
   * and #46, and a sort that moved a row from Waiting into Closed would delete
   * that answer to save a click.
   *
   * The Closed group is the twenty most recent, which the hub picked before this
   * ran (`briefGroups`, and the route returns newest first), so ordering it by
   * title orders those twenty rather than every job that ever closed.
   */
  const sorting = useSort(
    jobSortOptions((brief) =>
      countable && showsProgress(brief)
        ? briefProgress(tasksByBrief.get(brief.id) ?? [])
        : undefined,
    ),
    (brief) => brief.id,
  );
  const sorted = groups.map((group) => ({ ...group, briefs: sorting.sort(group.briefs) }));

  // One list to the keyboard, whatever it is grouped into. Stood down while
  // the board is the active view: its own roving focus is the one that
  // should hear `j`/`k` then, and two listeners on the same keys is neither.
  const { rowProps } = useRowKeys({
    // In the order the page prints them: the stalled drafts are the first
    // region on it.
    ids: [
      ...stalled.map((brief) => brief.id),
      ...sorted.flatMap((group) => group.briefs.map((brief) => brief.id)),
    ],
    enabled: !board,
  });

  // The task board, under the same "Jobs" title (issue #60). `TaskBoardSection`
  // owns every hook the board needs -- its own filters, sort and roving focus
  // -- so this is the one place it is mounted, and the render prop lets its
  // `sub` and filter menus land in this page's own header rather than a
  // second one stacked under it.
  if (board) {
    return (
      <TaskBoardSection>
        {(view) => (
          <>
            <ViewHeader
              title="Jobs"
              icon={ScrollText}
              tone="var(--dy-plan)"
              sub={view.sub}
              actions={
                <>
                  {toggle}
                  {view.actions}
                </>
              }
            />
            <Page>{view.body}</Page>
          </>
        )}
      </TaskBoardSection>
    );
  }

  return (
    <>
      <ViewHeader
        title="Jobs"
        icon={ScrollText}
        tone="var(--dy-plan)"
        sub={`${openBriefs(rows).length} open · ${rows.length} in total${
          search.repo ? ` in ${search.repo}` : ''
        }`}
        actions={
          <>
            {toggle}
            <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
            <NewBriefButton tone="hero" />
          </>
        }
      />
      <Page>
        <div className="dy-stack">
          <NeedsPlan briefs={rows} tasks={taskRows} rowProps={rowProps} />

          {briefs.isPending && !rows.length ? (
            <Section>
              <SectionHead title="Awaiting approval" />
              <Surface>
                <RowSkeleton rows={3} />
              </Surface>
            </Section>
          ) : briefs.isError && !briefs.data ? (
            // Not the empty state: "no jobs yet" over a hub that has hundreds
            // is worse than no answer at all.
            <Section>
              <SectionHead title="Jobs" />
              <Surface>
                <LoadError what="jobs" error={briefs.error} retry={() => void briefs.refetch()} />
              </Surface>
            </Section>
          ) : (
            sorted.map((group) => (
              <Section key={group.key}>
                <SectionHead
                  title={group.label}
                  meta={group.briefs.length ? String(group.briefs.length) : undefined}
                  hue={group.key === 'gate' && group.briefs.length ? 'var(--dy-gate)' : undefined}
                />
                <Surface>
                  {group.briefs.length ? (
                    <ul className="divide-line divide-y">
                      {group.briefs.map((brief) => (
                        <JobRow
                          key={brief.id}
                          brief={brief}
                          rowProps={rowProps(brief.id)}
                          progress={
                            countable && showsProgress(brief)
                              ? briefProgress(tasksByBrief.get(brief.id) ?? [])
                              : undefined
                          }
                          steps={
                            countable && showsProgress(brief)
                              ? (tasksByBrief.get(brief.id) ?? [])
                              : undefined
                          }
                          paused={(tasksByBrief.get(brief.id) ?? []).some((task) =>
                            Boolean(task.pausedAt),
                          )}
                          waiting={
                            group.key === 'waiting'
                              ? waitingReason(tasksByBrief.get(brief.id) ?? [])
                              : undefined
                          }
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="text-fg-3 px-gutter py-6 text-center text-sm">{group.empty}</p>
                  )}
                </Surface>
              </Section>
            ))
          )}

          <Failures tasks={taskRows} capped={taskRows.length >= TASK_LIST_LIMIT} />
        </div>
      </Page>
    </>
  );
}
